import { Theme, alpha } from '@mui/material/styles';
import { ThemeMode } from '~/types/config';
import { ButtonVariantProps, ColorProps, ExtendedStyleProps } from '~/types/extended';
import getColors from '~/utils/getColors';
import getShadow from '~/utils/getShadow';

interface ButtonStyleProps extends ExtendedStyleProps {
  variant: ButtonVariantProps;
}

const getCommonShadow = (main: string, dark: string) => {
  return {
    '&::after': {
      boxShadow: `0 0 5px 5px ${alpha(main, 0.9)}`,
    },
    '&:active::after': {
      boxShadow: `0 0 0 0 ${alpha(main, 0.9)}`,
    },
    '&:focus-visible': {
      outline: `2px solid ${dark}`,
      outlineOffset: 2,
    },
  };
};

function getColorStyle({ variant, color, theme }: ButtonStyleProps) {
  const colors = getColors(theme, color);
  const { lighter, main, dark, contrastText } = colors;

  const buttonShadow = `${color}Button`;
  const shadows = getShadow(theme, buttonShadow);

  const commonShadow = getCommonShadow(main, dark);

  switch (variant) {
    case 'contained':
      return {
        '&:hover': {
          backgroundColor: dark,
        },
        ...commonShadow,
      };
    case 'shadow':
      return {
        color: contrastText,
        backgroundColor: main,
        boxShadow: shadows,
        '&:hover': {
          boxShadow: 'none',
          backgroundColor: dark,
        },
        ...commonShadow,
      };
    case 'outlined':
      return {
        borderColor: main,
        '&:hover': {
          color: dark,
          backgroundColor: 'transparent',
          borderColor: dark,
        },
        ...commonShadow,
      };
    case 'dashed':
      return {
        color: main,
        borderColor: main,
        backgroundColor: lighter,
        '&:hover': {
          color: dark,
          borderColor: dark,
        },
        ...commonShadow,
      };
    case 'text':
    default:
      return {
        '&:hover': {
          color: dark,
          backgroundColor: lighter,
        },
        ...commonShadow,
      };
  }
}

function getContainedStyles({ color, theme }: { color: ColorProps; theme: Theme }) {
  const colors = getColors(theme, color);
  const { main, dark, contrastText } = colors;

  const commonShadow = getCommonShadow(main, dark);

  return {
    backgroundColor: main,
    border: `1px solid ${alpha(main, 0.5)}`,
    color: contrastText,
    '&:hover': {
      backgroundColor: dark,
      border: `1px solid ${alpha(main, 0.5)}`,
    },
    '&:disabled': {
      borderColor: `#595959 !important`,
    },
    ...commonShadow,
  };
}

function getOutlinedStyles({ color, theme }: { color: ColorProps; theme: Theme }) {
  const colors = getColors(theme, color);
  const { main, dark } = colors;

  const commonShadow = getCommonShadow(main, dark);

  return {
    color: main,
    border: `1px solid ${alpha(main, 0.5)}`,
    borderColor: main,
    backgroundColor: 'transparent',
    '&:hover': {
      color: dark,
      backgroundColor: 'transparent',
      borderColor: dark,
    },
    '&:disabled': {
      borderColor: `#595959 !important`,
    },
    ...commonShadow,
  };
}

export default function Button(theme: Theme) {
  const disabledStyle = {
    '&.Mui-disabled': {
      backgroundColor: theme.palette.grey[200],
    },
  };
  const iconStyle = {
    '&>*:nth-of-type(1)': {
      fontSize: 'inherit',
    },
  };

  return {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          fontWeight: 400,
          '&::after': {
            content: '""',
            display: 'block',
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            borderRadius: 4,
            opacity: 0,
            transition: 'all 0.5s',
          },

          '&:active::after': {
            position: 'absolute',
            borderRadius: 4,
            left: 0,
            top: 0,
            opacity: 1,
            transition: '0s',
          },
        },
        contained: {
          ...disabledStyle,
        },
        outlined: {
          ...disabledStyle,
        },
        text: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        endIcon: {
          ...iconStyle,
        },
        startIcon: {
          ...iconStyle,
        },
        dashed: {
          border: '1px dashed',
          ...getColorStyle({ variant: 'dashed', color: 'primary', theme }),
          '&.MuiButton-dashedPrimary': getColorStyle({
            variant: 'dashed',
            color: 'primary',
            theme,
          }),
          '&.MuiButton-dashedSecondary': getColorStyle({
            variant: 'dashed',
            color: 'secondary',
            theme,
          }),
          '&.MuiButton-dashedError': getColorStyle({ variant: 'dashed', color: 'error', theme }),
          '&.MuiButton-dashedSuccess': getColorStyle({
            variant: 'dashed',
            color: 'success',
            theme,
          }),
          '&.MuiButton-dashedInfo': getColorStyle({ variant: 'dashed', color: 'info', theme }),
          '&.MuiButton-dashedWarning': getColorStyle({
            variant: 'dashed',
            color: 'warning',
            theme,
          }),
          '&.Mui-disabled': {
            color: `${theme.palette.grey[300]} !important`,
            borderColor: `${theme.palette.grey[400]} !important`,
            backgroundColor: `${theme.palette.grey[200]} !important`,
          },
        },
        shadow: {
          ...getColorStyle({ variant: 'shadow', color: 'primary', theme }),
          '&.MuiButton-shadowPrimary': getColorStyle({
            variant: 'shadow',
            color: 'primary',
            theme,
          }),
          '&.MuiButton-shadowSecondary': getColorStyle({
            variant: 'shadow',
            color: 'secondary',
            theme,
          }),
          '&.MuiButton-shadowError': getColorStyle({ variant: 'shadow', color: 'error', theme }),
          '&.MuiButton-shadowSuccess': getColorStyle({
            variant: 'shadow',
            color: 'success',
            theme,
          }),
          '&.MuiButton-shadowInfo': getColorStyle({ variant: 'shadow', color: 'info', theme }),
          '&.MuiButton-shadowWarning': getColorStyle({
            variant: 'shadow',
            color: 'warning',
            theme,
          }),
          '&.Mui-disabled': {
            color: `${theme.palette.grey[300]} !important`,
            borderColor: `${theme.palette.grey[400]} !important`,
            backgroundColor: `${theme.palette.grey[200]} !important`,
          },
        },
        /* In dark mode, swap both variant AND color for primary/secondary:
         *   contained-primary (blue filled)    -> outlined-secondary (white outline)
         *   outlined-primary (blue outline)    -> contained-secondary (white filled)
         *   contained-secondary (white filled) -> outlined-primary (blue outline)
         *   outlined-secondary (grey outline)  -> contained-primary (blue filled)
         *
         * Note:
         * Please refer to ticket TRMT-696 for more details on the these changes.
         */
        containedPrimary:
          theme.palette.mode === ThemeMode.DARK
            ? getOutlinedStyles({ color: 'secondary', theme })
            : getContainedStyles({ color: 'primary', theme }),
        outlinedPrimary:
          theme.palette.mode === ThemeMode.DARK
            ? getContainedStyles({ color: 'secondary', theme })
            : getOutlinedStyles({ color: 'primary', theme }),
        containedSecondary:
          theme.palette.mode === ThemeMode.DARK
            ? getOutlinedStyles({ color: 'primary', theme })
            : getContainedStyles({ color: 'secondary', theme }),
        outlinedSecondary:
          theme.palette.mode === ThemeMode.DARK
            ? getContainedStyles({ color: 'primary', theme })
            : getOutlinedStyles({ color: 'secondary', theme }),
        // Other colors remain unchanged
        containedError: getContainedStyles({ color: 'error', theme }),
        containedSuccess: getContainedStyles({ color: 'success', theme }),
        containedInfo: getContainedStyles({ color: 'info', theme }),
        containedWarning: getContainedStyles({ color: 'warning', theme }),
        outlinedError: getOutlinedStyles({ color: 'error', theme }),
        outlinedSuccess: getOutlinedStyles({ color: 'success', theme }),
        outlinedInfo: getOutlinedStyles({ color: 'info', theme }),
        outlinedWarning: getOutlinedStyles({ color: 'warning', theme }),
        textPrimary: getColorStyle({ variant: 'text', color: 'primary', theme }),
        textSecondary: getColorStyle({ variant: 'text', color: 'secondary', theme }),
        textError: getColorStyle({ variant: 'text', color: 'error', theme }),
        textSuccess: getColorStyle({ variant: 'text', color: 'success', theme }),
        textInfo: getColorStyle({ variant: 'text', color: 'info', theme }),
        textWarning: getColorStyle({ variant: 'text', color: 'warning', theme }),
        sizeExtraSmall: {
          minWidth: 56,
          fontSize: '0.625rem',
          padding: '2px 8px',
        },
      },
    },
  };
}
